import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { ROUTES } from '../../constants/routes';

const AuthCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        console.log("Auth callback - redirecting to home page");
        
        // Refresh the user session to ensure we have the latest data
        await refreshUser();
        
        // Clear any stored redirect paths
        try {
          sessionStorage.removeItem('auth_redirect');
        } catch (e) {
          console.warn('Failed to clear auth redirect from session storage');
        }
        
        // Handle proper navigation based on environment
        const baseUrl = window.location.origin;
        const isLocalDevelopment = baseUrl.includes('localhost') || 
          baseUrl.includes('127.0.0.1') || 
          baseUrl.match(/\d+\.\d+\.\d+\.\d+/);
        
        // After a short delay to ensure the auth state is updated
        setTimeout(() => {
          if (isLocalDevelopment) {
            console.log("Development environment detected, navigating to:", ROUTES.HOME);
            navigate(ROUTES.HOME, { replace: true });
          } else {
            // For production, use full URL without hash
            const destinationUrl = new URL(ROUTES.HOME, window.location.origin).toString();
            console.log("Production environment detected, navigating to:", destinationUrl);
            window.location.href = destinationUrl;
          }
        }, 1000);
      } catch (err: any) {
        console.error('Error handling auth callback:', err);
        setError('Authentication failed. Please try again.');
        
        setTimeout(() => {
          navigate(ROUTES.AUTH, { replace: true });
        }, 3000);
      }
    };
    
    handleAuthCallback();
  }, [navigate, refreshUser]);
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50">
      <div className="max-w-md w-full px-4">
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          {error ? (
            <>
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </div>
              <h2 className="text-2xl font-serif font-bold mb-2">Authentication Error</h2>
              <p className="text-neutral-600">{error}</p>
              <p className="text-neutral-500 mt-4">Redirecting you back to login...</p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 mx-auto mb-4 relative">
                <div className="absolute inset-0 rounded-full border-4 border-neutral-200"></div>
                <div className="absolute inset-0 rounded-full border-t-4 border-r-4 border-primary-500 animate-spin"></div>
              </div>
              <h2 className="text-2xl font-serif font-bold mb-2">Signing you in</h2>
              <p className="text-neutral-600">Please wait while we complete the authentication...</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthCallbackPage;